import React, { useEffect, useRef, useState } from "react"
import "./AddCommonCourse.scss"
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Calendar } from "primereact/calendar"
import { useNavigate } from "react-router-dom"
import CommonCourseApi from "Api/CommonCourseApi"
import { Toast } from 'primereact/toast'

const AddCommonCourse = () => {
  document.title = "Add Common Course"
  const [loading, setLoading] = useState(false)
  const toast = useRef(null)
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")
  const userData = JSON.parse(localStorage.getItem("userData"))

  const [courseData, setCourseData] = useState({
    courseName: "",
    courseOverview: {
      overview: "",
      courseHighlights: ""
    },
    courseCareerOutcome: {
      careerOutcome: "",
      jobRoles: ""
    },
    courseRequirements: {
      eligibility: "",
      medicalAdmission: "",
      admissionRequirements: ""
    },
    courseExam: [],
    faq: [],
    createdBy: userData?.email || ""
  })

  const [currentExam, setCurrentExam] = useState({
    examName: "",
    examDescription: "",
    examDuration: "",
    examDate: "",
    examLocation: "",
    examRegistration: "",
    examFee: ""
  })

  const [currentFaq, setCurrentFaq] = useState({
    question: "",
    answer: ""
  })

  const handleAddExam = () => {
    if (!currentExam.examName || !currentExam.examDescription || !currentExam.examDuration || !currentExam.examDate || !currentExam.examRegistration) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill all required exam fields',
        life: 3000
      }
        
    )
      return
    }
    setCourseData(prev => ({
      ...prev,
      courseExam: [...prev.courseExam, currentExam]
    }))
    setCurrentExam({
      examName: "",
      examDescription: "",
      examDuration: "",
      examDate: "",
      examLocation: "",
      examRegistration: "",
      examFee: ""
    })
  }

  const handleAddFaq = () => {
    if (!currentFaq.question || !currentFaq.answer) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill both question and answer',
        life: 3000
      })
      return
    }
    setCourseData(prev => ({
      ...prev,
      faq: [...prev.faq, currentFaq]
    }))
    setCurrentFaq({
      question: "",
      answer: ""
    })
  }

  const handleSubmit = () => {
    if (!courseData.courseName || !courseData.courseOverview.overview || !courseData.courseOverview.courseHighlights ||
        !courseData.courseCareerOutcome.careerOutcome || !courseData.courseCareerOutcome.jobRoles ||
        !courseData.courseRequirements.eligibility || !courseData.courseRequirements.medicalAdmission ||
        !courseData.courseRequirements.admissionRequirements) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill all required fields',
        life: 3000
      })
      return
    }

    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    CommonCourseApi.createCommonCourse(courseData, config)
      .then(response => {
        if (response.data.status) {
          toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Common Course added successfully',
            life: 3000
          })
          navigate("/commonCourseList")
        }
      })
      .catch(error => {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || 'Failed to add common course',
          life: 3000
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Common Courses", link: "#" },
    { label: "Add Common Course", link: "#" },
  ]

  return (
    <>
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="common-course-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="Add Common Course" />
          </div>

          <Card className="mt-4">
            <CardBody>
              <div className="common-course-form">
                <h5 className="mb-4">Basic Information</h5>
                <div className="form-group mb-3">
                  <label htmlFor="courseName">Course Name</label>
                  <InputText
                    id="courseName"
                    value={courseData.courseName}
                    onChange={(e) => setCourseData(prev => ({ ...prev, courseName: e.target.value }))}
                    className="form-control"
                  />
                </div>

                <h5 className="mb-4 mt-4">Course Overview</h5>
                <div className="form-group mb-3">
                  <label htmlFor="overview">Overview</label>
                  <InputTextarea
                    id="overview"
                    value={courseData.courseOverview.overview}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseOverview: { ...prev.courseOverview, overview: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="courseHighlights">Course Highlights</label>
                  <InputTextarea
                    id="courseHighlights"
                    value={courseData.courseOverview.courseHighlights}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseOverview: { ...prev.courseOverview, courseHighlights: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>

                <h5 className="mb-4 mt-4">Career Outcomes</h5>
                <div className="form-group mb-3">
                  <label htmlFor="careerOutcome">Career Outcome</label>
                  <InputTextarea
                    id="careerOutcome"
                    value={courseData.courseCareerOutcome.careerOutcome}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseCareerOutcome: { ...prev.courseCareerOutcome, careerOutcome: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="jobRoles">Job Roles</label>
                  <InputTextarea
                    id="jobRoles"
                    value={courseData.courseCareerOutcome.jobRoles}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseCareerOutcome: { ...prev.courseCareerOutcome, jobRoles: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>

                <h5 className="mb-4 mt-4">Course Requirements</h5>
                <div className="form-group mb-3">
                  <label htmlFor="eligibility">Eligibility</label>
                  <InputTextarea
                    id="eligibility"
                    value={courseData.courseRequirements.eligibility}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseRequirements: { ...prev.courseRequirements, eligibility: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="medicalAdmission">Medical Admission</label>
                  <InputTextarea
                    id="medicalAdmission"
                    value={courseData.courseRequirements.medicalAdmission}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseRequirements: { ...prev.courseRequirements, medicalAdmission: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="admissionRequirements">Admission Requirements</label>
                  <InputTextarea
                    id="admissionRequirements"
                    value={courseData.courseRequirements.admissionRequirements}
                    onChange={(e) => setCourseData(prev => ({
                      ...prev,
                      courseRequirements: { ...prev.courseRequirements, admissionRequirements: e.target.value }
                    }))}
                    rows={5}
                    className="form-control"
                  />
                </div>

                <h5 className="mb-4 mt-4">Exams</h5>
                <div className="exam-form mb-4">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="examName">Exam Name</label>
                      <InputText
                        id="examName"
                        value={currentExam.examName}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examName: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="examDuration">Exam Duration</label>
                      <InputText
                        id="examDuration"
                        value={currentExam.examDuration}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examDuration: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="examDate">Exam Date</label>
                      <input
                        type="date"
                        id="examDate"
                        value={currentExam.examDate}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examDate: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="examLocation">Exam Location</label>
                      <InputText
                        id="examLocation"
                        value={currentExam.examLocation}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examLocation: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="examRegistration">Exam Registration</label>
                      <InputText
                        id="examRegistration"
                        value={currentExam.examRegistration}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examRegistration: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="examFee">Exam Fee</label>
                      <InputText
                        id="examFee"
                        value={currentExam.examFee}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examFee: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <label htmlFor="examDescription">Exam Description</label>
                      <InputTextarea
                        id="examDescription"
                        value={currentExam.examDescription}
                        onChange={(e) => setCurrentExam(prev => ({ ...prev, examDescription: e.target.value }))}
                        rows={3}
                        className="form-control"
                      />
                    </div>
                    <div className="col-12">
                      <Button
                        type="button"
                        color="primary"
                        className="waves-effect"
                        onClick={handleAddExam}
                      >
                        Add Exam
                      </Button>
                    </div>
                  </div>
                </div>

                {courseData.courseExam.length > 0 && (
                  <div className="added-exams mb-4">
                    <h6>Added Exams:</h6>
                    {courseData.courseExam.map((exam, index) => (
                      <div key={index} className="exam-item p-2 border rounded mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{exam.examName}</strong>
                            <p className="mb-0">Date: {new Date(exam.examDate).toLocaleDateString()}</p>
                            <p className="mb-0">Duration: {exam.examDuration}</p>
                            <p className="mb-0">Location: {exam.examLocation || "N/A"}</p>
                          </div>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={() => {
                              setCourseData(prev => ({
                                ...prev,
                                courseExam: prev.courseExam.filter((_, i) => i !== index)
                              }))
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <h5 className="mb-4 mt-4">FAQs</h5>
                <div className="faq-form mb-4">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="faqQuestion">Question</label>
                      <InputText
                        id="faqQuestion"
                        value={currentFaq.question}
                        onChange={(e) => setCurrentFaq(prev => ({ ...prev, question: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="faqAnswer">Answer</label>
                      <InputText
                        id="faqAnswer"
                        value={currentFaq.answer}
                        onChange={(e) => setCurrentFaq(prev => ({ ...prev, answer: e.target.value }))}
                        className="form-control"
                      />
                    </div>
                    <div className="col-12">
                      <Button
                        type="button"
                        color="primary"
                        className="waves-effect"
                        onClick={handleAddFaq}
                      >
                        Add FAQ
                      </Button>
                    </div>
                  </div>
                </div>

                {courseData.faq.length > 0 && (
                  <div className="added-faqs mb-4">
                    <h6>Added FAQs:</h6>
                    {courseData.faq.map((faq, index) => (
                      <div key={index} className="faq-item p-2 border rounded mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>Q: {faq.question}</strong>
                            <p className="mb-0">A: {faq.answer}</p>
                          </div>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={() => {
                              setCourseData(prev => ({
                                ...prev,
                                faq: prev.faq.filter((_, i) => i !== index)
                              }))
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="button"
                    color="secondary"
                    className="waves-effect me-2"
                    onClick={() => navigate("/commonCourseList")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    className="waves-effect"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "Submit"}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default AddCommonCourse 